// De acht verantwoordingsindicatoren van de SiSa/SPUK-regeling energiearmoede
// voor jaar T (T23 van docs/features/huishouden-vaste-gegevens.md). Puur: de
// widget haalt de gegevens op, dit rekent.
//
//   1 huishoudens in een huurwoning met voorzieningen in jaar T
//   2 idem, koopwoning
//   3 besteding jaar T voor 1+2: materiaal (prijs bij registratie × aantal) + uren uitvoering × tarief
//   4 idem cumulatief t/m jaar T
//   5 huishoudens met advies aan huis in jaar T
//   6 besteding advies jaar T: uren advies × tarief
//   7 idem cumulatief t/m jaar T
//   8 maatregelen per categorie (sisaMeasureCategory)
//
// KEUZES (vastgelegd in de spec, T23):
// - 'Voorziening' = een gebruikte (`done`) registratie van een materiaal, geen
//   advies, met een registratiedatum in het jaar. Advies telt bij 5.
// - De arbeid bij 3/4 telt alleen voor de huishoudens uit 1+2, die bij 6/7 alleen
//   voor die uit 5: het geld hoort bij de getelde huishoudens. Cumulatief = de
//   huishoudens die in enig jaar t/m T meetelden, met al hun uren t/m T.
// - Eén maatregel (8) = één materiaal bij één huishouden in jaar T.
// - Eigendom komt alleen uit het vaste veld. Een huishouden zonder eigendom
//   staat in `eigendomOnbekend`, niet stil nergens.
// - Zonder tarief zijn arbeid en totaal `null`; zonder uren is de arbeid 0.
// AI-CONTEXT: See docs/pitfalls.md#ui-aggregate-collapses-nothing-into-zero

import readHouseholdField from '../adressHelpers/householdFields'
import householdEffort from '../adressHelpers/householdEffort'
import { readHourlyRates } from '../adressHelpers/hourlyRates'
import resolveAppointmentKinds from '../tasks/resolveAppointmentKinds'
import { unitPriceFor } from '../materialsHelpers/computeHouseholdMaterials'
import materialQuantities from '../materialsHelpers/materialQuantities'
import { readRegistrationDate } from '../materialsHelpers/registrationDate'
import { isAdvice } from '../materialsHelpers/materialCatalog'
import sisaMeasureCategory, { SISA_MEASURE_CATEGORIES } from '../materialsHelpers/sisaMeasureCategory'
import { toDayKeyInTimeZone } from '../dateHelpers/adressDashboard/tasksHours'

/**
 * @typedef {object} HouseholdSet
 * @property {number} count
 * @property {Array<string>} adressIDs - Gesorteerd, voor de lijst achter het getal
 */

/**
 * @typedef {object} Spending
 * @property {number} materiaal
 * @property {number|null} arbeid - null = tarief niet ingesteld terwijl er uren zijn
 * @property {number|null} totaal
 */

/**
 * @typedef {object} SisaIndicators
 * @property {number} year
 * @property {HouseholdSet} huur - Indicator 1
 * @property {HouseholdSet} koop - Indicator 2
 * @property {Spending} besteding - Indicator 3
 * @property {HouseholdSet} huurCumulatief
 * @property {HouseholdSet} koopCumulatief
 * @property {Spending} bestedingCumulatief - Indicator 4
 * @property {HouseholdSet} advies - Indicator 5
 * @property {number|null} adviesBesteding - Indicator 6
 * @property {number|null} adviesBestedingCumulatief - Indicator 7
 * @property {Record<string, number>} maatregelen - Indicator 8, per categorie
 * @property {HouseholdSet} eigendomOnbekend - Huishoudens met een voorziening in T zonder eigendom
 * @property {number} prijsIsHuidig - Regels in 3 die met de huidige prijs rekenen
 * @property {number} prijsIsHuidigCumulatief - Idem in 4
 * @property {{uitvoering: boolean, advies: boolean}} tariefOntbreekt
 */

const isDeleted = (doc) => doc?.Deleted === true || doc?.Deleted === 'true'

/** @param {number|null} ms @returns {number|null} Het jaar in Nederlandse tijd */
const yearOf = (ms) => (ms === null ? null : Number(String(toDayKeyInTimeZone(ms)).slice(0, 4)))

/** @param {Set<string>} ids @returns {HouseholdSet} */
const asSet = (ids) => ({ count: ids.size, adressIDs: [...ids].sort() })

/**
 * @param {number} hours
 * @param {number|null} rate
 * @returns {number|null}
 */
const labourCost = (hours, rate) => {
    if (!hours) return 0
    return rate === null ? null : hours * rate
}

/** @param {number} materiaal @param {number|null} arbeid @returns {Spending} */
const spending = (materiaal, arbeid) => ({ materiaal, arbeid, totaal: arbeid === null ? null : materiaal + arbeid })

/**
 * @param {object} input
 * @param {Array<object>} input.adresses - `Adresses` van de organisatie
 * @param {Array<object>} input.adressMaterials - `AdressMaterials` van de organisatie
 * @param {Array<object>} input.materials - `Materials`, óók verwijderde (een regel kan ernaar wijzen)
 * @param {Array<object>} input.tasks - `Tasks` van de organisatie
 * @param {object} [input.meta] - `CompagnyMeta` (tarieven en soorten afspraak)
 * @param {string} [input.brand] - Merknaam: bepaalt de ingebouwde soorten afspraak
 * @param {number} input.year - Verantwoordingsjaar T
 * @param {Date} [input.now] - Wat 'voorbij' is voor afspraken
 * @returns {SisaIndicators}
 */
const computeSisaIndicators = ({ adresses = [], adressMaterials = [], materials = [], tasks = [], meta, brand = '', year, now = new Date() }) => {
    const rates = readHourlyRates(meta)
    // Ook verborgen soorten: een afspraak van een soort die nu verborgen is,
    // is wel gewerkt.
    const kinds = resolveAppointmentKinds(meta, { brand, includeHidden: true })

    const adressByID = new Map(adresses.filter((a) => a?.ID && !isDeleted(a)).map((a) => [a.ID, a]))
    const materialByID = new Map(materials.filter((m) => m?.ID).map((m) => [m.ID, m]))

    const huur = new Set()
    const koop = new Set()
    const huurTot = new Set()
    const koopTot = new Set()
    const advies = new Set()
    const adviesTot = new Set()
    const onbekend = new Set()
    /** @type {Record<string, Set<string>>} */
    const maatregelen = Object.fromEntries(SISA_MEASURE_CATEGORIES.map((c) => [c.key, new Set()]))
    /** Materiaal per huishouden, apart voor jaar T en t/m T. */
    const materiaalJaar = new Map()
    const materiaalTot = new Map()
    const huidigJaar = new Map()
    const huidigTot = new Map()
    const add = (map, id, n) => map.set(id, (map.get(id) || 0) + n)

    adressMaterials.forEach((entry) => {
        if (!entry || isDeleted(entry)) return
        const adress = adressByID.get(entry.AdressID)
        if (!adress) return
        const aantallen = materialQuantities(entry)
        if (aantallen.status !== 'done') return
        const entryYear = yearOf(readRegistrationDate(entry).ms)
        if (entryYear === null || entryYear > year) return
        const inJaar = entryYear === year
        const material = materialByID.get(entry.MaterialID)

        if (inJaar) maatregelen[sisaMeasureCategory(material)].add(`${adress.ID}|${entry.MaterialID}`)

        if (isAdvice(material)) {
            adviesTot.add(adress.ID)
            if (inJaar) advies.add(adress.ID)
            return
        }

        const eigendom = readHouseholdField(adress, undefined, 'Eigendom')
        const isKoop = eigendom === 'koop'
        const isHuur = eigendom === 'huur-corporatie' || eigendom === 'huur-particulier'
        if (!isKoop && !isHuur) {
            if (inJaar) onbekend.add(adress.ID)
            return
        }
        ;(isKoop ? koopTot : huurTot).add(adress.ID)
        if (inJaar) (isKoop ? koop : huur).add(adress.ID)

        const { unitPrice, prijsIsHuidig } = unitPriceFor(entry, material)
        const bedrag = unitPrice * aantallen.impact
        add(materiaalTot, adress.ID, bedrag)
        if (prijsIsHuidig) add(huidigTot, adress.ID, 1)
        if (inJaar) {
            add(materiaalJaar, adress.ID, bedrag)
            if (prijsIsHuidig) add(huidigJaar, adress.ID, 1)
        }
    })

    const tasksByAdress = new Map()
    tasks.forEach((t) => {
        if (!t?.AdressID) return
        if (!tasksByAdress.has(t.AdressID)) tasksByAdress.set(t.AdressID, [])
        tasksByAdress.get(t.AdressID).push(t)
    })
    const effortOf = new Map()
    const effort = (id) => {
        if (!effortOf.has(id)) effortOf.set(id, householdEffort(tasksByAdress.get(id) || [], kinds, rates, year, now))
        return effortOf.get(id)
    }
    /** @param {Set<string>} ids @param {'year'|'cumulative'} span @param {'uitvoering'|'advies'} inzet */
    const hoursOf = (ids, span, inzet) => [...ids].reduce((sum, id) => sum + effort(id)[span][inzet].hours, 0)
    /** @param {Map<string, number>} map @param {Set<string>} ids */
    const sumOver = (map, ids) => [...ids].reduce((sum, id) => sum + (map.get(id) || 0), 0)

    const jaarIDs = new Set([...huur, ...koop])
    const totIDs = new Set([...huurTot, ...koopTot])

    return {
        year,
        huur: asSet(huur),
        koop: asSet(koop),
        besteding: spending(sumOver(materiaalJaar, jaarIDs), labourCost(hoursOf(jaarIDs, 'year', 'uitvoering'), rates.uitvoering)),
        huurCumulatief: asSet(huurTot),
        koopCumulatief: asSet(koopTot),
        bestedingCumulatief: spending(sumOver(materiaalTot, totIDs), labourCost(hoursOf(totIDs, 'cumulative', 'uitvoering'), rates.uitvoering)),
        advies: asSet(advies),
        adviesBesteding: labourCost(hoursOf(advies, 'year', 'advies'), rates.advies),
        adviesBestedingCumulatief: labourCost(hoursOf(adviesTot, 'cumulative', 'advies'), rates.advies),
        maatregelen: Object.fromEntries(Object.entries(maatregelen).map(([k, s]) => [k, s.size])),
        eigendomOnbekend: asSet(onbekend),
        prijsIsHuidig: sumOver(huidigJaar, jaarIDs),
        prijsIsHuidigCumulatief: sumOver(huidigTot, totIDs),
        tariefOntbreekt: { uitvoering: rates.uitvoering === null, advies: rates.advies === null },
    }
}

export default computeSisaIndicators
